import { Github, Linkedin } from '@/components/icons';
import type { PersonCard as PersonCardProps } from '@/types';

// The /id/:slug card of an un-themed edition (PersonCard in
// src/features/person-id/person-profile-page.tsx). Which column each link came from is
// resolved by PersonCardResource, so this only renders what it is handed.
export function PersonProfilePage({ person }: { person: PersonCardProps }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="mx-auto w-full max-w-md">
          <PersonCard person={person} />
        </div>
      </div>
    </div>
  );
}

function PersonCard({ person }: { person: PersonCardProps }) {
  return (
    <div className="border-2 border-primary bg-background/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4 p-6 sm:p-8">
        {person.avatarUrl && (
          <div className="relative h-24 w-24 overflow-hidden border-2 border-primary sm:h-32 sm:w-32">
            <img
              src={person.avatarUrl}
              alt={person.fullName}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        )}

        <div className="text-center">
          <span className="inline-block bg-primary px-3 py-1 font-bold font-title text-background text-xs tracking-wider sm:text-sm">
            {person.title}
          </span>
        </div>

        <div className="text-center">
          <h1 className="font-bold font-title text-2xl text-primary sm:text-3xl">
            {person.fullName}
          </h1>
          {person.githubUsername && (
            <p className="mt-1 font-mono text-primary/70 text-sm sm:text-base">
              @{person.githubUsername}
            </p>
          )}
        </div>

        {person.subtitle && (
          <p className="text-center font-mono text-primary/70 text-sm">
            {person.subtitle}
          </p>
        )}
      </div>

      <PersonCardLinks person={person} titleFont />
    </div>
  );
}

export function PersonCardLinks({
  person,
  titleFont = false,
}: {
  person: PersonCardProps;
  titleFont?: boolean;
}) {
  const { githubUrl, linkedinUrl, xUrl } = person;

  if (!githubUrl && !linkedinUrl && !xUrl) return null;

  const linkClass = `flex w-full items-center justify-center gap-2 border-2 border-primary px-4 py-3 font-bold ${
    titleFont ? 'font-title ' : ''
  }text-primary text-sm transition-colors hover:bg-primary hover:text-background`;

  return (
    <div className="flex flex-col gap-3 border-primary/20 border-t p-6 sm:p-8">
      {githubUrl && (
        <a
          href={githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          <Github className="h-5 w-5" />
          GitHub
        </a>
      )}
      {linkedinUrl && (
        <a
          href={linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          <Linkedin className="h-5 w-5" />
          LinkedIn
        </a>
      )}
      {xUrl && (
        <a
          href={xUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="h-5 w-5 fill-current"
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          X
        </a>
      )}
    </div>
  );
}
